import { FlightFilterType } from 'constants/flight'
import type { FlightSortOptions } from 'constants/flight'
import type { ValueOf } from 'type-fest'
import { removeUnusedFilters } from 'lib/flights/flightFilterUtils'
import type { FlightsFiltersValues } from 'lib/flights/flightFilterTypes'
import { FlightsFiltersRecommendedType } from 'lib/flights/flightFilterTypes'
import { standaloneFlightsFiltersInitialState } from 'lib/flights/standaloneFlightsFiltersInitialState'

export enum StandaloneFlightsFiltersAction {
  SET_FLIGHTS_FILTER = 'SET_FLIGHTS_FILTER',
  SET_FLIGHTS_SORT = 'SET_FLIGHTS_SORT',
  RE_INITIALISE = 'RE_INITIALISE',
  RESET_FILTERS = 'RESET_FILTERS',
  RESET_FILTER = 'RESET_FILTER',
  TOGGLE_NON_EXISTENT_FILTERS = 'TOGGLE_NON_EXISTENT_FILTERS',
  CLEAR_FILTER_VALUE = 'CLEAR_FILTER_VALUE',
}

export type StandaloneFlightsFiltersActions = ValueOf<Utils.FullActionMap<{
  [StandaloneFlightsFiltersAction.SET_FLIGHTS_FILTER]: { filters: Partial<FlightsFiltersValues> }
  [StandaloneFlightsFiltersAction.SET_FLIGHTS_SORT]: { sort: FlightSortOptions }
  [StandaloneFlightsFiltersAction.RESET_FILTERS]: {}
  [StandaloneFlightsFiltersAction.RESET_FILTER]: { filterType: FlightFilterType }
  [StandaloneFlightsFiltersAction.RE_INITIALISE]: { initialState: Partial<FlightsFiltersValues> }
  [StandaloneFlightsFiltersAction.TOGGLE_NON_EXISTENT_FILTERS]: { filters: Partial<FlightsFiltersValues> }
  [StandaloneFlightsFiltersAction.CLEAR_FILTER_VALUE]: { filterType: FlightFilterType, value: string }
}>>

export interface StandaloneFlightFiltersState extends FlightsFiltersValues {}

const initialState: StandaloneFlightFiltersState = standaloneFlightsFiltersInitialState

function standaloneFlightFiltersReducer(state: StandaloneFlightFiltersState, action: StandaloneFlightsFiltersActions): StandaloneFlightFiltersState {
  switch (action.type) {
    case StandaloneFlightsFiltersAction.SET_FLIGHTS_FILTER:
      return {
        ...state,
        ...action.filters,
      }

    case StandaloneFlightsFiltersAction.RESET_FILTERS:
      // priceFilterType is derived from the search's traveller mix, not a user-facing filter,
      // so resetting filters must not revert a mixed traveller search back to per-adult pricing.
      return {
        ...initialState,
        priceFilterType: state.priceFilterType,
      }

    case StandaloneFlightsFiltersAction.RE_INITIALISE:
      return {
        ...initialState,
        ...action.initialState,
      }

    case StandaloneFlightsFiltersAction.RESET_FILTER:
      // priceFilterType is derived from the search's traveller mix, not a user-facing filter
      // (see RESET_FILTERS above), so a targeted reset must leave it untouched too.
      if (action.filterType === FlightFilterType.PRICE_FILTER_TYPE) {
        return state
      }

      return {
        ...state,
        [action.filterType]: initialState[action.filterType as keyof FlightsFiltersValues],
      }

    case StandaloneFlightsFiltersAction.TOGGLE_NON_EXISTENT_FILTERS: {
      const newFilters = Object.keys(action.filters).reduce((filters, filterKey) => ({
        ...filters,
        [filterKey]: removeUnusedFilters(state[filterKey as keyof FlightsFiltersValues] || [], action.filters[filterKey as keyof FlightsFiltersValues] as Array<string>),
      }), {} as Partial<FlightsFiltersValues>)

      return {
        ...state,
        ...newFilters,
      }
    }

    case StandaloneFlightsFiltersAction.SET_FLIGHTS_SORT: {
      return {
        ...state,
        sort: action.sort,
      }
    }

    case StandaloneFlightsFiltersAction.CLEAR_FILTER_VALUE: {
      const existingFilterValue = state[action.filterType as keyof FlightsFiltersValues]
      const isArray = Array.isArray(existingFilterValue)

      // The "Direct flights" recommended shortcut is kept in sync with the stops filter
      // but stores its own value ('directFlights') rather than the underlying stops value
      // ('0'). Map the cleared value across so removing the stops chip also clears the
      // shortcut, otherwise the recommended filter stays applied.
      let recommendedValueToRemove: string = action.value
      if (action.filterType === FlightFilterType.STOPS && action.value === '0') {
        recommendedValueToRemove = FlightsFiltersRecommendedType.DIRECT_FLIGHTS
      }

      return {
        ...state,
        [action.filterType]: isArray ? existingFilterValue?.filter(value => value !== action.value) : undefined,
        recommended: state.recommended.filter(value => value !== recommendedValueToRemove),
      }
    }

    default:
      return state
  }
}

export default standaloneFlightFiltersReducer
